import { ref } from "vue";
import { defineStore } from "pinia";
import router from "@/router";

import { useConsoleLog as log } from "@/composables/console.js";
import { useUserStore } from "@/stores/user";
import Comm from "@/composables/comm.js";
import Misc from "@/composables/misc.js";

export const useAppStore = defineStore("app", () => {
  let dataItems = {};
  let currentAsset = null;
  const languages = ref([]);
  const transcriptionLanguages = ref([]);
  const translationLanguages = ref([]);
  const collections = ref([]);
  const templates = ref([]);
  const subtitleStyles = ref([]);
  const brandings = ref([]);
  const users = ref([]);
  const externalStorages = ref([]);
  const socialMediaAccounts = ref([]);
  const compositions = ref([]);
  const dataLoadStatus = {};
  const subtitleTypes = ["vtt", "srt", "stl"];
  const MAX_CLIP_LENGTH_MINUTES = Infinity; // CIR-3288
  const fonts = [
    "Arial",
    "Verdana",
    "Trebuchet MS",
    "Times New Roman",
    "Georgia",
    "Courier New",
    "Tahoma",
    "Garamond",
    "Montserrat",
    "Sofia Sans",
    "Roboto",
    "Open Sans",
    "Lato",
    "Oswald",
    "Poppins",
    "Playfair Display",
    "Merriweather",
    "Mulish",
    "Source Sans 3",
    "National Park",
    "Nunito",
    "Mulish"
  ];
  const aspectRatios = [
    {
      label: "9:16",
      value: "9:16",
      color: "#ff0000",
      icons: ["/img/instagram.svg", "/img/tiktok.svg", "/img/youtubeShorts.svg"]
    },
    { label: "3:4", value: "3:4", color: "#0059ff", icons: [] },
    {
      label: "4:5",
      value: "4:5",
      color: "#00ffff",
      icons: ["/img/instagram.svg"]
    },
    {
      label: "1:1",
      value: "1:1",
      color: "#ffff00",
      icons: ["/img/instagram.svg"]
    },
    { label: "4:3", value: "4:3", color: "#ffffff", icons: [] },
    { label: "16:9", value: "16:9", color: "#00ff00", icons: [] }
  ];
  const storageProviders = [
    { value: "azure", label: "Azure Blob Storage" },
    { value: "aws_s3", label: "Amazon S3" },
    { value: "gcp_storage", label: "Google Cloud Storage" }
  ];
  const env = import.meta.env?.VITE_ENVIRONMENT || "dev";

  const mode = ref(window.self === window.top ? "standalone" : "embedded");
  const apiKey = ref(null); // Used in embedded mode

  /**
   * Retrieves the list of supported languages from the API.
   */
  async function loadLanguages(forceUpdate = false) {
    while (dataLoadStatus.languages == "loading") await Misc.sleep(1000);
    if (dataLoadStatus.languages && !forceUpdate) return;

    dataLoadStatus.languages = "loading";
    const res = await Comm.get({ path: "/languages" });

    languages.value = res.data;
    dataLoadStatus.languages = "loaded";

    log("Languages loaded", res.data);
  }

  /**
   * Retrieves the list of available languages for this tenant
   */
  async function loadTenantLanguages(forceUpdate = false) {
    while (dataLoadStatus.tenantLanguages == "loading") await Misc.sleep(1000);
    if (dataLoadStatus.tenantLanguages && !forceUpdate) return;

    const idTenant = useUserStore().idTenant;
    if (!idTenant) return;

    dataLoadStatus.tenantLanguages = "loading";
    const res = await Comm.get({
      path: "/tenants/" + idTenant
    });
    transcriptionLanguages.value = res.data.transcription_languages;
    translationLanguages.value = res.data.translation_languages;
    dataLoadStatus.tenantLanguages = "loaded";

    log("Translation / Transcription languages loaded");
  }

  /**
   * Retrieves the list of available collections from the API.
   */
  async function loadCollections(forceUpdate = false) {
    while (dataLoadStatus.collections == "loading") await Misc.sleep(1000);
    if (dataLoadStatus.collections && !forceUpdate) return;

    dataLoadStatus.collections = "loading";
    const data = await Comm.harvest({ path: "/collections" });
    collections.value = data;
    dataLoadStatus.collections = true;

    log("Collections loaded", data);
  }

  async function loadCompositions(forceUpdate = false) {
    while (dataLoadStatus.compositions == "loading") await Misc.sleep(1000);
    if (dataLoadStatus.compositions && !forceUpdate) return;

    dataLoadStatus.compositions = "loading";
    const data = await Comm.harvest({ path: "/video_compositions" });
    compositions.value = data;
    dataLoadStatus.compositions = "loaded";

    log("Compositions loaded", data);
  }

  /**
   * Retrieves the list of available templates from the API.
   */
  async function loadTemplates(forceUpdate = false) {
    while (dataLoadStatus.templates == "loading") await Misc.sleep(1000);
    if (dataLoadStatus.templates && !forceUpdate) return;

    dataLoadStatus.templates = "loading";
    const data = await Comm.harvest({ path: "/templates" });

    // Order templates by id
    templates.value = [...data].sort((a, b) => a.id - b.id);

    // Until this field is provided by API, set default_template_id to true in the template that has the lowest id
    if (templates.value.length) {
      templates.value[0].default_template_id = true;
    }

    dataLoadStatus.templates = "loaded";
    log("Templates loaded", data);
  }

  /**
   * Retrieves the list of available subtitle styles from the API.
   */
  async function loadSubtitleStyles(forceUpdate = false) {
    while (dataLoadStatus.subtitleStyles == "loading") await Misc.sleep(1000);
    if (dataLoadStatus.subtitleStyles && !forceUpdate) return;

    dataLoadStatus.subtitleStyles = "loading";
    const data = await Comm.harvest({ path: "/branding_subtitle_styles" });

    // Order styles by name
    subtitleStyles.value = [...data].sort((a, b) => a.name - b.name);
    dataLoadStatus.subtitleStyles = "loaded";
    log("Subtitle styles loaded", data);
  }

  /**
   * Retrieves the list of available brandings from the API.
   */
  async function loadBrandings(forceUpdate = false) {
    while (dataLoadStatus.brandings == "loading") await Misc.sleep(1000);
    if (dataLoadStatus.brandings && !forceUpdate) return;

    dataLoadStatus.brandings = "loading";
    const data = await Comm.harvest({ path: "/brandings" });

    brandings.value = data;
    dataLoadStatus.brandings = "loaded";
    log("Brandings loaded", data);
  }

  /**
   * Retrieves the list of available external storages from the API.
   */
  async function loadExternalStorages(forceUpdate = false) {
    while (dataLoadStatus.externalStorages == "loading") await Misc.sleep(1000);
    if (dataLoadStatus.externalStorages && !forceUpdate) return;

    dataLoadStatus.externalStorages = "loading";
    const data = await Comm.harvest({ path: "/external_storages" });

    externalStorages.value = data;
    dataLoadStatus.externalStorages = "loaded";
    log("External storages loaded", data);
  }

  /**
   * Retrieves the list of available social media accounts from the API.
   */
  async function loadSocialMediaAccounts(forceUpdate = false) {
    while (dataLoadStatus.socialMediaAccounts == "loading")
      await Misc.sleep(1000);
    if (dataLoadStatus.socialMediaAccounts && !forceUpdate) return;

    dataLoadStatus.socialMediaAccounts = "loading";
    const data = await Comm.harvest({ path: "/social_media/accounts" });

    socialMediaAccounts.value = data;
    dataLoadStatus.socialMediaAccounts = "loaded";
    log("Social media accounts loaded", data);
  }

  /**
   * Retrieves the list of available users from the API.
   */
  async function loadUsers(forceUpdate = false) {
    while (dataLoadStatus.users == "loading") await Misc.sleep(1000);
    if (dataLoadStatus.users && !forceUpdate) return;

    dataLoadStatus.users = "loading";
    const res = await Comm.get({ path: "/users" });

    users.value = res.data;
    dataLoadStatus.users = "loaded";
  }

  /**
   * Returns the name of the language corresponding to the provided language ID.
   * If the language ID is not found, it returns the ID itself.
   *
   * @param {string|number} languageId - The ID of the language to look up.
   * @returns {string|number} The name of the language or the ID if not found.
   */
  function langName(languageId) {
    const language = languages.value.find(l => l.id == languageId);

    return language ? language.name : languageId;
  }

  /**
   * Returns the ISO 639-1 code of the language corresponding to the provided language ID.
   * If the language ID is not found, it returns the ID itself.
   *
   * @param {string|number} languageId - The ID of the language to look up.
   * @returns {string|number} The ISO 639-1 code of the language or the ID if not found.
   */
  function langCode(languageId) {
    const language = languages.value.find(l => l.id == languageId);

    return language ? language.iso_639_1 : languageId;
  }

  function isLangRtl(languageId) {
    const language = languages.value.find(l => l.id == languageId);

    return language ? language.rtl : false;
  }

  function setup() {
    // Save API token if provided via URL or custom options
    const apiKey = new URLSearchParams(window.location.search).get("otp");
    if (apiKey) {
      sessionStorage.setItem("token", apiKey);
    }

    // Load data items from LS
    dataItems = JSON.parse(localStorage.getItem("dataItems") || "{}");

    if (mode.value == "standalone") return;

    // The app is embedded in an iframe.

    // Show the "API required" page
    router.push("/embedded");

    log("🖼️ Embedded mode. Checking for API key provider");

    // Listen to any messages from parent window
    window.addEventListener("message", parseExternalMessage);

    // Now request API key from parent window
    window.parent.postMessage(
      {
        type: "C21MEDIACOPILOT_API_KEY_REQUEST"
      },
      "*"
    );
  }

  /**
   * This function is called when a message is received from the parent window.
   * It parses the message and perform any required actions.
   * This used for integrations via iframe with other C21 Live products.
   * @param {MessageEvent} e - The event sent by the parent window.
   */
  function parseExternalMessage(e) {
    if (e.source !== window.parent) {
      log("Ignoring external message received from unkown source.");
      return;
    }

    const msg = e.data;
    log(msg);

    switch (msg.type) {
      case "C21MEDIACOPILOT_API_KEY_RESPONSE":
        if (msg.data.apiKey) {
          log("Saving API key provided by Live Editor");
          apiKey.value = msg.data.apiKey;
          useUserStore().updateData({ thenGoTo: "/app/assets" });
        }
        break;

      case "C21MEDIACOPILOT_OPEN_ASSET":
        if (msg.data.assetId) {
          log("Live Editor requested opening asset", msg.data.assetId);
          router.push("/app/assets/" + msg.data.assetId);
        }
        break;
    }
  }

  /**
   * Returns a data item by type and ID. Data items are pieces of data like the
   * name of a user, a template, a collection, etc. It is used to avoid making
   * unnecessary API calls.
   * @param {string} type - The type of the data item.
   * @param {string|number} id - The ID of the data item.
   * @returns {object|null} The data item or null if not found.
   */
  async function dataItem(type, id) {
    if (!type || !id) {
      return null;
    }
    if (dataItems[type]?.[id]) {
      return dataItems[type][id];
    }
    try {
      const apiCall = { path: "" };
      switch (type) {
        case "user":
          if (users.value.find(u => u.id == id)) {
            return users.value.find(u => u.id == id);
          }
          apiCall.path = "/users/" + id;
          break;
        case "template":
          if (templates.value.find(t => t.id == id)) {
            return templates.value.find(t => t.id == id);
          }
          apiCall.path = "/templates/" + id;
          break;
        case "collection":
          if (collections.value.find(c => c.id == id)) {
            return collections.value.find(c => c.id == id);
          }
          apiCall.path = "/collections/" + id;
          break;
        case "language":
          if (languages.value.find(l => l.id == id)) {
            return languages.value.find(l => l.id == id);
          }
          // Callers may pass an ISO 639-1 code (e.g. "es", "eu") instead of the
          // language UUID; resolve those locally so we don't hit the UUID-keyed
          // endpoint with a code, which the backend rejects with a 422.
          if (languages.value.find(l => l.iso_639_1 == id)) {
            return languages.value.find(l => l.iso_639_1 == id);
          }
          apiCall.path = "/languages/" + id;
          break;
        case "branding":
          if (brandings.value.find(b => b.id == id)) {
            return brandings.value.find(b => b.id == id);
          }
          apiCall.path = "/brandings/" + id;
          break;
        case "subtitleStyle":
          if (subtitleStyles.value.find(ss => ss.id == id)) {
            return subtitleStyles.value.find(ss => ss.id == id);
          }
          apiCall.path = "/branding_subtitle_styles/" + id;
          break;
        case "composition":
          if (compositions.value.find(c => c.id == id)) {
            return compositions.value.find(c => c.id == id);
          }
          apiCall.path = "/video_compositions/" + id;
          break;
        case "socialMediaAccount":
          if (socialMediaAccounts.value.find(s => s.id == id)) {
            return socialMediaAccounts.value.find(s => s.id == id);
          }
          apiCall.path = "/social_media/accounts/" + id;
          break;
      }
      // The endpoints above are keyed by an opaque id (UUID, or a numeric id).
      // A non-numeric string that is not a UUID (e.g. an ISO language code like
      // "es"/"eu" that could not be resolved locally) can only be a mistake: the
      // backend rejects it with a 422 that surfaces as a global error banner.
      // Skip the call and let callers handle the null return with their fallback.
      const looksLikeId = Misc.isValidUuid(id) || /^\d+$/.test(String(id));
      if (apiCall.path && !looksLikeId) {
        return null;
      }
      if (apiCall.path) {
        const res = await Comm.get({
          path: apiCall.path,
          ignoreErrorCodes: [404]
        });
        if (res) {
          if (!dataItems[type]) dataItems[type] = {};
          dataItems[type][id] = res.data;
          localStorage.setItem("dataItems", JSON.stringify(dataItems));
          return res.data;
        }
      }
    } catch (error) {
      log("Error fetching data item:", error);
    }

    return null;
  }

  /**
   * Sets a data item by type and ID.
   * @param {string} type - The type of the data item.
   * @param {object} data - The data item.
   */
  function setDataItem(type, data) {
    if (!dataItems[type]) dataItems[type] = {};
    dataItems[type][data.id] = data;
    localStorage.setItem("dataItems", JSON.stringify(dataItems));
  }

  setup();

  return {
    MAX_CLIP_LENGTH_MINUTES,
    subtitleTypes,
    fonts,
    aspectRatios,
    languages,
    transcriptionLanguages,
    translationLanguages,
    langName,
    langCode,
    isLangRtl,
    collections,
    loadCollections,
    templates,
    loadTemplates,
    subtitleStyles,
    loadSubtitleStyles,
    brandings,
    loadBrandings,
    externalStorages,
    loadExternalStorages,
    users,
    loadUsers,
    loadLanguages,
    loadTenantLanguages,
    compositions,
    loadCompositions,
    socialMediaAccounts,
    loadSocialMediaAccounts,
    dataLoadStatus,
    mode,
    apiKey,
    env,
    storageProviders,
    dataItem,
    setDataItem,
    currentAsset
  };
});
